import { Container } from '@/components/layout/Container';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { mediaBreakpointUp, paddingX, paddingY } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import styled from 'styled-components';

const HeaderWrapper = styled.div<{ backgroundColor?: string }>`
  background-color: ${(props) => props.backgroundColor ?? 'transparent'};
  ${(props) => paddingY(props.backgroundColor ? 50 : 0)}

  ${mediaBreakpointUp('desktop')} {
    ${(props) => paddingY(props.backgroundColor ? 80 : 0)}
  }
`;

const Header = styled.div`
  margin-bottom: ${remCalc(80)};
  text-align: center;
`;

const Heading = styled.h2`
  ${customTextSize(32, 38)}
  letter-spacing: -0.01em;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(30)};
`;

const TableWrapper = styled.div<{ backgroundColor?: string }>`
  background-color: ${(props) => props.backgroundColor ?? 'transparent'};
  ${(props) => paddingY(props.backgroundColor ? 40 : 0)}

  ${Container} {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  & + & {
    margin-top: ${remCalc(40)};
  }
`;

const Eyebrow = styled.h5<{ onBackground: boolean }>`
  display: inline-block;
  ${customTextSize(14, 18)}
  font-weight: ${FONT_WEIGHTS.BOLD};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: ${remCalc(10, 20)};
  margin: 0 auto ${remCalc(20)};
  background-color: ${(props) => (props.onBackground ? hexToRGBA(COLORS.WHITE, 0.8) : COLORS.GRAY.HIGHLIGHT)};
  border-radius: ${remCalc(10)};
`;

const Description = styled.div`
  ${customTextSize(16, 24)}
  margin: 0 auto ${remCalc(40)};
`;

const Table = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${remCalc(30)};

  ${mediaBreakpointUp('desktop')} {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: ${remCalc(20)};
  }
`;

interface ColumnProps {
  onBackground: boolean;
  joinColumns: boolean;
  backgroundColor?: string;
  columnCount: number;
}

const Column = styled.div<ColumnProps>`
  position: relative;
  background-color: ${(props) => hexToRGBA(props.backgroundColor ?? 'transparent', props.onBackground ? 0.8 : 1)};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(40, 20)};
  z-index: 1;

  &:last-of-type::before {
    content: '';
    position: absolute;
    width: 100%;
    height: ${remCalc(60)};
    background-color: ${(props) => props.backgroundColor ?? 'transparent'};
    top: ${remCalc(-40)};
    left: 0;
    z-index: 0;
    display: ${(props) => (props.joinColumns ? 'block' : 'none')};
  }

  ${mediaBreakpointUp('tablet')} {
    ${paddingX(30)}
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column-end: span ${(props) => 12 / props.columnCount};
    ${paddingX(20)}

    &:last-of-type::before {
      height: 100%;
      width: ${remCalc(60)};
      left: ${remCalc(-40)};
      top: 0;
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${paddingX(30)}
  }
`;

const ColumnHeading = styled.h6`
  ${customTextSize(24, 30)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  letter-spacing: -0.01em;
  margin-bottom: ${remCalc(30)};
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${remCalc(30)};
`;

const ListItem = styled.li`
  position: relative;
  ${customTextSize(18, 26)}
  padding-left: ${remCalc(40)};

  svg {
    position: absolute;
    top: 0;
    left: 0;
    width: ${remCalc(24)};
  }

  ${mediaBreakpointUp('tablet')} {
    padding-left: ${remCalc(48)};

    svg {
      width: ${remCalc(32)};
    }
  }
`;

const ColumnFooter = styled.div`
  margin: 30px 0 0 0;
  ${customTextSize(12, 14)}

  p + p {
    margin-top: 6px;
  }
`;

const textListTableComponents = {
  HeaderWrapper,
  Heading,
  Header,
  TableWrapper,
  Eyebrow,
  Description,
  Table,
  Column,
  ColumnHeading,
  ColumnFooter,
  List,
  ListItem,
};

export default textListTableComponents;
